import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Switch, FormControlLabel,
  Table, TableBody, TableHead, TableRow, TableCell,
  IconButton, MenuItem, Select, FormControl, InputLabel,
} from "@material-ui/core";
import { Add, DeleteOutline } from "@material-ui/icons";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const DoctorSchema = Yup.object().shape({
  name: Yup.string().required("Obrigatório"),
  specialization: Yup.string().required("Obrigatório"),
  consultationDuration: Yup.number().min(5).max(120),
});

const DAY_OPTIONS = [
  { value: 1, label: "Segunda-feira" },
  { value: 2, label: "Terça-feira" },
  { value: 3, label: "Quarta-feira" },
  { value: 4, label: "Quinta-feira" },
  { value: 5, label: "Sexta-feira" },
  { value: 6, label: "Sábado" },
  { value: 0, label: "Domingo" },
];

const DAY_NAMES = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const initialDoctor = { name: "", specialization: "", phone: "", email: "", bio: "", consultationDuration: 30, active: true };

const DoctorModal = ({ open, onClose, doctor, onSave }) => {
  const [doctorData, setDoctorData] = useState(initialDoctor);
  const [savedDoctorId, setSavedDoctorId] = useState(null);
  const [availabilities, setAvailabilities] = useState([]);
  const [newAvail, setNewAvail] = useState({ dayOfWeek: 1, startTime: "08:00", endTime: "18:00", breakStart: "12:00", breakEnd: "13:00" });

  useEffect(() => {
    if (doctor) {
      setDoctorData({ ...initialDoctor, ...doctor });
      setSavedDoctorId(doctor.id);
      loadAvailabilities(doctor.id);
    } else {
      setDoctorData(initialDoctor);
      setSavedDoctorId(null);
      setAvailabilities([]);
    }
  }, [doctor, open]);

  const loadAvailabilities = async (docId) => {
    try {
      const { data } = await api.get(`/clinic/doctors/${docId}/availability`);
      setAvailabilities(data);
    } catch (err) { toastError(err); }
  };

  const handleSave = async (values) => {
    try {
      let saved;
      if (savedDoctorId) {
        const { data } = await api.put(`/clinic/doctors/${savedDoctorId}`, values);
        saved = data;
      } else {
        const { data } = await api.post("/clinic/doctors", values);
        saved = data;
        setSavedDoctorId(data.id);
      }
      toast.success(savedDoctorId ? "Médico atualizado" : "Médico cadastrado");
      if (onSave) onSave(saved);
      if (!savedDoctorId) return; // keep modal open for availability
      onClose();
    } catch (err) { toastError(err); }
  };

  const handleAddAvail = async () => {
    if (!savedDoctorId) { toast.warning("Salve o médico primeiro"); return; }
    try {
      const { data } = await api.post("/clinic/availability", { ...newAvail, doctorId: savedDoctorId });
      setAvailabilities(prev => [...prev, data]);
      toast.success("Horário adicionado");
    } catch (err) { toastError(err); }
  };

  const handleDeleteAvail = async (id) => {
    try {
      await api.delete(`/clinic/availability/${id}`);
      setAvailabilities(prev => prev.filter(a => a.id !== id));
      toast.success("Horário removido");
    } catch (err) { toastError(err); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{savedDoctorId ? "Editar Médico" : "Novo Médico"}</DialogTitle>
      <Formik initialValues={doctorData} enableReinitialize validationSchema={DoctorSchema} onSubmit={handleSave}>
        {({ touched, errors, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <Field as={TextField} name="name" label="Nome" variant="outlined" size="small" fullWidth error={touched.name && !!errors.name} helperText={touched.name && errors.name} />
                <Field as={TextField} name="specialization" label="Especialidade" variant="outlined" size="small" fullWidth error={touched.specialization && !!errors.specialization} helperText={touched.specialization && errors.specialization} />
                <Field as={TextField} name="phone" label="Telefone" variant="outlined" size="small" fullWidth />
                <Field as={TextField} name="email" label="E-mail" variant="outlined" size="small" fullWidth />
                <Field as={TextField} name="consultationDuration" label="Duração (min)" type="number" variant="outlined" size="small" fullWidth />
                <FormControlLabel
                  control={<Switch checked={values.active} onChange={e => setFieldValue("active", e.target.checked)} color="primary" />}
                  label="Ativo"
                />
              </div>
              <Field as={TextField} name="bio" label="Bio / Sobre" variant="outlined" size="small" fullWidth multiline minRows={2} style={{ marginBottom: 16 }} />

              {/* Disponibilidade */}
              <div style={{ borderTop: "1px solid #e0e0e0", paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Horários de Atendimento</div>
                {!savedDoctorId && <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>Salve o médico primeiro para configurar horários.</div>}

                {savedDoctorId && (
                  <>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Dia</TableCell>
                          <TableCell>Início</TableCell>
                          <TableCell>Fim</TableCell>
                          <TableCell>Intervalo</TableCell>
                          <TableCell align="center" width={50}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availabilities.map(a => (
                          <TableRow key={a.id}>
                            <TableCell>{DAY_NAMES[a.dayOfWeek]}</TableCell>
                            <TableCell>{a.startTime}</TableCell>
                            <TableCell>{a.endTime}</TableCell>
                            <TableCell>{a.breakStart && a.breakEnd ? `${a.breakStart}-${a.breakEnd}` : "-"}</TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => handleDeleteAvail(a.id)}><DeleteOutline fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                        <InputLabel>Dia</InputLabel>
                        <Select value={newAvail.dayOfWeek} onChange={e => setNewAvail(p => ({ ...p, dayOfWeek: e.target.value }))} label="Dia">
                          {DAY_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField type="time" size="small" variant="outlined" label="Início" InputLabelProps={{ shrink: true }} value={newAvail.startTime} onChange={e => setNewAvail(p => ({ ...p, startTime: e.target.value }))} />
                      <TextField type="time" size="small" variant="outlined" label="Fim" InputLabelProps={{ shrink: true }} value={newAvail.endTime} onChange={e => setNewAvail(p => ({ ...p, endTime: e.target.value }))} />
                      <TextField type="time" size="small" variant="outlined" label="Intervalo início" InputLabelProps={{ shrink: true }} value={newAvail.breakStart} onChange={e => setNewAvail(p => ({ ...p, breakStart: e.target.value }))} />
                      <TextField type="time" size="small" variant="outlined" label="Intervalo fim" InputLabelProps={{ shrink: true }} value={newAvail.breakEnd} onChange={e => setNewAvail(p => ({ ...p, breakEnd: e.target.value }))} />
                      <Button variant="outlined" size="small" startIcon={<Add />} onClick={handleAddAvail}>Adicionar</Button>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancelar</Button>
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>Salvar</Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default DoctorModal;
